import React, { memo, useState, useEffect } from 'react';
import { Stack, Card, Typography, Divider, Chip } from 'investira.react.components';
import MdEditor from './MdEditor';
import QuestaoRating from './QuestaoRating';
import AvaliadoCheck from './AvaliadoCheck';
import { formats, validators } from 'investira.sdk';
const pvObterLabelDominioPorIndice = (pRespostaDominio, pIndiceResposta) => {
  if (!Array.isArray(pRespostaDominio) || pRespostaDominio.length === 0) {
    return null;
  }
  const xIndice = Number(pIndiceResposta);
  const xIndiceDominio = xIndice - 1;
  if (!Number.isInteger(xIndice) || xIndiceDominio < 0) {
    return null;
  }
  const xItemDominio = pRespostaDominio[xIndiceDominio];
  if (typeof xItemDominio === 'string') {
    return xItemDominio;
  }
  return xItemDominio?.label || null;
};
const pvObterPosicaoPorNota = (pRespostaDominio = [], pNota) => {
  const xNota = Number(pNota);
  if (!Array.isArray(pRespostaDominio) || pRespostaDominio.length === 0 || !Number.isFinite(xNota)) {
    return null;
  }
  const xIndex = pRespostaDominio.findIndex(pItem => Number(pItem?.nota) === xNota);
  return xIndex >= 0 ? xIndex : null;
};
const pvObterPosicaoPorRespostaGestor = (pQuestao = {}, pRespostaGestor = {}) => {
  const xRespostaDominio = Array.isArray(pQuestao?.resposta_dominio) ? pQuestao.resposta_dominio : [];
  const xRespostaTipo = pQuestao?.resposta_tipo;
  const xValorRespostaGestor = pRespostaGestor?.valor;
  if (xRespostaDominio.length === 0 || xValorRespostaGestor === null || xValorRespostaGestor === undefined || xValorRespostaGestor === '') {
    return null;
  }
  if (xRespostaTipo === 'NUM') {
    const xValorNumerico = Number(xValorRespostaGestor);
    if (!Number.isFinite(xValorNumerico)) {
      return null;
    }
    const xIndex = xRespostaDominio.findIndex(pItem => {
      const xMinimoValido = pItem?.minimo == null || xValorNumerico >= Number(pItem.minimo);
      const xMaximoValido = pItem?.maximo == null || xValorNumerico <= Number(pItem.maximo);
      return xMinimoValido && xMaximoValido;
    });
    return xIndex >= 0 ? xIndex : null;
  }
  const xValorNormalizado = String(xValorRespostaGestor);
  const xIndex = xRespostaDominio.findIndex(pItem => String(pItem?.valor) === xValorNormalizado || String(pItem?.id) === xValorNormalizado);
  return xIndex >= 0 ? xIndex : null;
};
const pvFormatarRespostaNumerica = pValor => {
  const xValorNumerico = Number(pValor);
  if (!Number.isFinite(xValorNumerico)) {
    return null;
  }
  return formats.formatNumber(xValorNumerico, 2, true, false);
};

const pvIsQuestaoInternaRevisada = pValue => {
  return pValue?.revisor_usuario_id != null && pValue?.revisor_nota != null;
};

const pvResolverChecked = (pIsQuestaoInterna, pValue = {}) => {
  if (pIsQuestaoInterna) {
    return pvIsQuestaoInternaRevisada(pValue);
  }

  return pValue?.revisor_usuario_id != null;
};

const QuestaoCard = memo(props => {
  const {
    id,
    data,
    activeQuestionId,
    aliasId,
    onSelect,
    value = {},
    respostaGestor = {},
    revisorUsuarioId = null,
    onChange
  } = props;
  const isSelected = Number(activeQuestionId) === id;
  const xIsQuestaoInterna = data?.interna === true || respostaGestor?.interna === true;
  const xRespostaGestorDominio = Array.isArray(data?.resposta_dominio) ? data.resposta_dominio : [];
  const xMaxRating = Math.max(xRespostaGestorDominio.length - 1, 0);
  const [markdown, setMarkdown] = useState('');
  const [rating, setRating] = useState(null);
  const [checked, setChecked] = useState(pvResolverChecked(xIsQuestaoInterna, value));
  const pvObterNotaPorPosicao = pPosicao => {
    const xItemDominio = xRespostaGestorDominio[Number(pPosicao)];
    const xNota = Number(xItemDominio?.nota);
    return Number.isFinite(xNota) ? xNota : null;
  };
  const pvMontarPayloadRevisao = (pPayload = {}, pPosicaoRating = rating) => {
    const xNota = pvObterNotaPorPosicao(pPosicaoRating);
    return {
      ...pPayload,
      ...(xNota !== null && {
        revisor_nota: xNota
      })
    };
  };
  const hasJustificativa = !validators.isEmpty(String(markdown).trim());
  const xRespostaGestorLabel = data?.resposta_tipo === 'MUL' || data?.resposta_tipo === 'DIC' ? pvObterLabelDominioPorIndice(xRespostaGestorDominio, respostaGestor?.valor) : null;
  const xRespostaGestorNumerica = data?.resposta_tipo === 'NUM' ? pvFormatarRespostaNumerica(respostaGestor?.valor) : null;
  const xRespostaGestorTexto = !validators.isEmpty(xRespostaGestorLabel) ? String(xRespostaGestorLabel) : !validators.isEmpty(xRespostaGestorNumerica) ? String(xRespostaGestorNumerica) : !validators.isEmpty(respostaGestor?.label) ? String(respostaGestor.label) : validators.isEmpty(respostaGestor?.valor) ? '-' : String(respostaGestor.valor);
  const pvAplicarAutoCheck = (pPayload = {}, pMarkdown = markdown, pRating = rating) => {
    const xHasJustificativa = !validators.isEmpty(String(pMarkdown).trim());
    const xHasRating = pRating !== null && pRating !== undefined;
    const xPodeAutoMarcar = xIsQuestaoInterna ? xHasRating : xHasJustificativa && xHasRating;

    if (!checked && xPodeAutoMarcar) {
      setChecked(true);
      return {
        ...pPayload,
        revisor_usuario_id: revisorUsuarioId
      };
    }
    return pPayload;
  };
  const handleMarkdownChange = pMarkdown => {
    setMarkdown(pMarkdown);
    onChange?.(id, pvMontarPayloadRevisao(pvAplicarAutoCheck({
      revisor_justificativa: pMarkdown
    }, pMarkdown)));
  };
  useEffect(() => {
    const xRatingResolvido = pvObterPosicaoPorNota(xRespostaGestorDominio, value?.revisor_nota) ?? pvObterPosicaoPorRespostaGestor(data, respostaGestor);
    setMarkdown(value?.revisor_justificativa || '');
    setRating(xRatingResolvido);
    setChecked(pvResolverChecked(xIsQuestaoInterna, value));
  }, [data, id, respostaGestor, value?.revisor_justificativa, value?.revisor_nota, value?.revisor_usuario_id, xRespostaGestorDominio, xIsQuestaoInterna]);
  const handleRatingChange = pRating => {
    const xNota = pvObterNotaPorPosicao(pRating);
    if (xNota === null) {
      return;
    }
    setRating(pRating);
    onChange?.(id, pvAplicarAutoCheck({
      revisor_nota: xNota
    }, markdown, pRating));
  };
  const handleCheckedChange = pChecked => {
    if (xIsQuestaoInterna && pChecked && (rating === null || rating === undefined)) {
      return;
    }

    setChecked(pChecked);
    onChange?.(id, pvMontarPayloadRevisao({
      revisor_usuario_id: pChecked ? revisorUsuarioId : null
    }));
  };
  const xExibirRating = xRespostaGestorDominio.length > 0 && (hasJustificativa || xIsQuestaoInterna);
  const xVistoDesabilitado =
    !isSelected || (xIsQuestaoInterna && (rating === null || rating === undefined));
  return <Card id={id} sx={{
    opacity: isSelected ? 1 : 0.7,
    ...(isSelected && {
      border: '1px solid',
      borderColor: 'primary.main'
    })
  }} raised={isSelected} onClick={() => onSelect?.(id)}>
      <Stack m={2}>
        <Stack mb={1} direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" gap={1} alignItems="center">
            <Typography variant="caption" color="textSecondary">
              Questão {aliasId}
            </Typography>
          </Stack>
        </Stack>

        <Stack>
          <Typography variant="body1" color="textPrimary" sx={{
          fontWeight: 600
        }} gutterBottom>
            {data.questao}
          </Typography>
          <Stack>
            {xIsQuestaoInterna ? <Chip label="Interna" size="small" color="secondary" sx={{
            width: 'fit-content'
          }} /> : <>
                <Typography variant="caption" color="textSecondary">
                  Resposta do gestor:
                </Typography>
                <Typography variant="body2" color="textPrimary" sx={{
              fontWeight: 500
            }}>
                  {xRespostaGestorTexto}
                </Typography>
              </>}
          </Stack>
        </Stack>
      </Stack>
      <Divider />
      <Stack m={2}>
        <Stack gap={1}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="caption" color="textSecondary">
              Justificativa
            </Typography>
          </Stack>
          <MdEditor id={id} markdown={markdown} onBlur={(e, id) => console.log(e, id)} isSelected={isSelected} onChange={handleMarkdownChange} />
        </Stack>
        <Stack mt={1}>
          <Stack direction="row" alignItems="center" sx={{
          minHeight: '40px'
        }} justifyContent={xExibirRating ? 'space-between' : 'end'}>
            {xExibirRating && <QuestaoRating name={`rt-questao-${id}`} rating={rating} max={xMaxRating} readOnly={!isSelected} onChange={handleRatingChange} />}
            <AvaliadoCheck checked={checked} disabled={xVistoDesabilitado} onChange={handleCheckedChange} />
          </Stack>
        </Stack>
      </Stack>
    </Card>;
});
export default QuestaoCard;
